import { formatDate } from "@angular/common";
import { AfterViewInit, Component, DoCheck, EventEmitter, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "metadata-input-time",
    templateUrl: "./metadata-input-time.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputTimeComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    locale = inject(LOCALE_ID);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);
    private readonly $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: Date | string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    dense: boolean;

    @Input()
    compact: boolean;

    @Input()
    forCellEdit: boolean;

    @Input()
    time24Hour: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @Input()
    showSpinner: boolean;

    private _valueModel: string;
    private VALID_TIME_PATTERN: RegExp = /^(1[0-2]|0?[1-9]):[0-5][0-9] (AM|PM)$/;
    public allowSeconds: boolean = false;

    private _initialized: boolean = false;

    /** Accessible in the template */ isViewOnly: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: Date | string;
    private readonly debouncer$ = new Subject<void>();
    private _density: string;

    constructor()
    {
        this.debouncer$.pipe(debounceTime(500), takeUntilDestroyed()).subscribe(() => this.fireValueChangedImmediate());
    }

    ngOnInit(): void
    {
        if (!this.onValueChanged)
        {
            console.warn(
                `${MetadataInputTimeComponent.name}|${this.propertyMetadata.Name}|onValueChanged method binding was not set!`
            );
        }

        const reg = this.$metadataPropertyService.getRegex(this.propertyMetadata, this.metadataModel, this.parent);
        if (!Utilities.IsNullOrUndefined(reg))
        {
            this.VALID_TIME_PATTERN = new RegExp(reg);

            if (this.VALID_TIME_PATTERN.test("12:00:00 AM"))
            {
                this.allowSeconds = true;
            }
        }

        this.updateValueModel(this.model);

        this._density = this.$metadataService.getInputDensity(this.compact);
    }

    ngOnDestroy(): void
    {
        this.debouncer$.complete();
    }

    ngAfterViewInit(): void
    {
        if (this._initialized)
            return;
        this._initialized = true;
    }

    ngDoCheck(): void
    {
        if (!this._initialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.updateValueModel(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        if (!this._initialized)
        {
            return;
        }
        const changedKeys = Object.keys(changesObj);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = changesObj[key];
                    if (!change.currentValue)
                    {
                        this.valueModel = null;
                        break;
                    }
                    this.updateValueModel(change.currentValue);
                    break;
            }
        }
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    private updateValueModel(newValue: Date | string): void
    {
        const currentDate = this.toDate(this.valueModel);
        const newModel = new Date(newValue);
        if (!currentDate || currentDate !== newModel)
        {
            this.unboundModel = newValue;
            this.valueModel = this.formatTime(newModel);
        }
    }

    get isRequired(): boolean
    {
        return this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) && !this.isViewOnly;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    onInputChange(): void
    {
        this.debouncer$.next();
    }

    get timeValid(): boolean
    {
        return this.time24Hour ? true : this.VALID_TIME_PATTERN.test(this.valueModel);
    }

    fireValueChangedImmediate(): void
    {
        if (this.forCellEdit && this.timeValid && this.onValueChanged)
        {
            this.fireValueChanged();
        }
    }

    fireValueChanged(): void
    {
        // I do not understand why the value Model is keeping the _ placeholders?
        if (this.valueModel)
            this.valueModel = this.valueModel.replace("_", "");
        let dt = null;
        if (Utilities.IsNullOrUndefined(this.model))
        {
            dt = new Date();
        }
        else
        {
            dt = new Date(this.model);
        }
        const modelAsDate = ObjectHelpers.isDate(dt) ? dt : this.toDate(this.model.toString());
        const currentDate = this.toDate(`${this.formatDate(dt)} ${this.valueModel}`);
        if (MetadataInputTimeComponent.areEqual(modelAsDate, currentDate))
        {
            if (currentDate)
            {
                // if the valueModel isn't correctly formatted, but is a parse-able date, then lets apply formatting to the text value.
                this.updateValueModel(currentDate);
            }
            return;
        }
        if (
            (this.valueModel && !currentDate) || // if we have a value that can't be turned into date value, don't push value up.
            !this.onValueChanged
        )
        {
            return;
        }
        if (!Utilities.IsNullOrUndefined(currentDate) && currentDate.getHours() === 0 && currentDate.getMinutes() === 0)
        {
            currentDate.setMinutes(0, 0, 1); //Update to handle midnight issue
        }
        requestAnimationFrame(() => this.onValueChanged.emit({ value: currentDate }));
    }

    private static areEqual(a: Date, b: Date): boolean
    {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getTime() === b.getTime();
    }

    private toDate(value: string): Date
    {
        const date = new Date(value);
        if (!date || isNaN(date.getTime())) return null;
        return date;
    }

    private formatDate(value: Date | string): string
    {
        if (isNaN(new Date(value)
            .getDate()))
        {
            return "";
        }
        return formatDate(value, "MM/dd/yyyy", this.locale);
    }

    private formatTime(value: Date | string): string
    {
        if (isNaN(new Date(value)
            .getTime()))
        {
            return "";
        }
        let format = "";

        if(this.time24Hour)
        {
            format = `HH:mm${this.allowSeconds === true ? ":ss" : ""}`;
        }
        else
        {
            format = `hh:mm${this.allowSeconds === true ? ":ss" : ""} a`;
        }

        return formatDate(value, format, this.locale);
    }

    onKeyDown(event: KeyboardEvent): void
    {
        if(event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }
}
